import React from 'react';
import { createRoot } from 'react-dom/client';
import { useEffect, useMemo, useState } from 'react';
import { PhoneInput, isPhoneValid } from '../../../PhoneInput/PhoneInput.js';
import { openPopupById } from './crmQuizHelpers.js';
import {
	buildQuestionnaireAnswerText,
	CONTEXT_EVENT,
	ensureQuestionnaireSession,
	getQuestionnaireSession,
	getQuestionnaireStatus,
	startQuestionnaireAutoSubmit,
	subscribeQuestionnaireStatus,
	submitQuestionnaireLead,
} from './crmQuizQuestionnaireFlow.js';

const translations = {
	ua: {
		title: 'Залиште свої дані',
		description: 'Якщо хочете, щоб консультант переглянув ваші відповіді, залиште контакти. Ми поєднаємо їх із результатами квізу.',
		name: "Ім'я*",
		lastname: 'Прізвище*',
		company: 'Компанія*',
		position: 'Посада*',
		email: 'E-mail*',
		phone: 'Номер телефону*',
		comment: 'Коментар',
		commentHint: '/ Не обов\'язково',
		acceptance:
			"Я погоджуюсь з <a href=\"https://www.smart-it.com/uk/terms-of-website-use/\" target=\"_blank\" rel=\"noreferrer\">умовами використання сайту</a> та <a href=\"https://www.smart-it.com/uk/privacy-policy/\" target=\"_blank\" rel=\"noreferrer\">політикою конфіденційності</a>",
		submit: 'Залишити запит',
		submitting: 'Відправляємо...',
		success: 'Дякуємо. Ми зв’яжемося з вами найближчим часом.',
		locked: 'Результат вже був автоматично надісланий у CRM. Додаткові контакти для цього квізу більше не приймаються.',
		required: 'Заповніть це поле.',
		emailError: 'Вкажіть коректний e-mail.',
		phoneError: 'Вкажіть коректний номер телефону.',
		acceptanceError: 'Потрібно прийняти умови.',
	},
	en: {
		title: 'Leave your details',
		description: 'If you want a consultant to review your answers, leave your contact details. We will combine them with your quiz result.',
		name: 'First name*',
		lastname: 'Last name*',
		company: 'Company*',
		position: 'Position*',
		email: 'E-mail*',
		phone: 'Phone number*',
		comment: 'Comment',
		commentHint: '/ Optional',
		acceptance:
			'I agree with the <a href="https://www.smart-it.com/en/terms-of-website-use/" target="_blank" rel="noreferrer">website terms</a> and <a href="https://www.smart-it.com/en/privacy-policy/" target="_blank" rel="noreferrer">privacy policy</a>',
		submit: 'Send request',
		submitting: 'Sending...',
		success: 'Thanks. We will contact you shortly.',
		locked: 'This result has already been sent to CRM automatically. Additional contact details are no longer accepted for this quiz.',
		required: 'Please fill in this field.',
		emailError: 'Please enter a valid e-mail address.',
		phoneError: 'Please enter a valid phone number.',
		acceptanceError: 'You need to accept the terms.',
	},
	pl: {
		title: 'Zostaw swoje dane',
		description: 'Jeśli chcesz, aby konsultant przejrzał Twoje odpowiedzi, zostaw dane kontaktowe. Połączymy je z wynikiem quizu.',
		name: 'Imię*',
		lastname: 'Nazwisko*',
		company: 'Firma*',
		position: 'Stanowisko*',
		email: 'E-mail*',
		phone: 'Numer telefonu*',
		comment: 'Komentarz',
		commentHint: '/ Opcjonalnie',
		acceptance:
			'Akceptuję <a href="https://www.smart-it.com/pl/terms-of-website-use/" target="_blank" rel="noreferrer">warunki korzystania ze strony</a> oraz <a href="https://www.smart-it.com/pl/privacy-policy/" target="_blank" rel="noreferrer">politykę prywatności</a>',
		submit: 'Wyślij zapytanie',
		submitting: 'Wysyłanie...',
		success: 'Dziękujemy. Skontaktujemy się z Tobą wkrótce.',
		locked: 'Ten wynik został już automatycznie wysłany do CRM. Dalsze dane kontaktowe nie są już przyjmowane.',
		required: 'Wypełnij to pole.',
		emailError: 'Podaj poprawny adres e-mail.',
		phoneError: 'Podaj poprawny numer telefonu.',
		acceptanceError: 'Musisz zaakceptować warunki.',
	},
	az: {
		title: 'Məlumatlarınızı buraxın',
		description: 'Mütəxəssis cavablarınızı nəzərdən keçirsin istəyirsinizsə, əlaqə məlumatlarınızı buraxın. Onları quiz nəticənizlə birləşdirəcəyik.',
		name: 'Ad*',
		lastname: 'Soyad*',
		company: 'Şirkət*',
		position: 'Vəzifə*',
		email: 'E-mail*',
		phone: 'Telefon nömrəsi*',
		comment: 'Şərh',
		commentHint: '/ Könüllü',
		acceptance:
			'Mən <a href="https://www.smart-it.com/az/terms-of-website-use/" target="_blank" rel="noreferrer">saytın istifadə şərtləri</a> və <a href="https://www.smart-it.com/az/privacy-policy/" target="_blank" rel="noreferrer">məxfilik siyasəti</a> ilə razıyam',
		submit: 'Sorğunu göndər',
		submitting: 'Göndərilir...',
		success: 'Təşəkkürlər. Tezliklə sizinlə əlaqə saxlayacağıq.',
		locked: 'Bu nəticə artıq avtomatik olaraq CRM-ə göndərilib. Bu quiz üçün əlavə əlaqə məlumatları artıq qəbul edilmir.',
		required: 'Bu sahəni doldurun.',
		emailError: 'Düzgün e-mail ünvanı daxil edin.',
		phoneError: 'Düzgün telefon nömrəsi daxil edin.',
		acceptanceError: 'Şərtləri qəbul etməlisiniz.',
	},
	ge: {
		title: 'დატოვეთ თქვენი მონაცემები',
		description: 'თუ გსურთ, რომ კონსულტანტმა თქვენი პასუხები განიხილოს, დატოვეთ საკონტაქტო მონაცემები. ჩვენ მათ quiz-ის შედეგთან გავაერთიანებთ.',
		name: 'სახელი*',
		lastname: 'გვარი*',
		company: 'კომპანია*',
		position: 'თანამდებობა*',
		email: 'E-mail*',
		phone: 'ტელეფონის ნომერი*',
		comment: 'კომენტარი',
		commentHint: '/ არასავალდებულო',
		acceptance:
			'ვეთანხმები <a href="https://www.smart-it.com/ge/terms-of-website-use/" target="_blank" rel="noreferrer">საიტის გამოყენების პირობებს</a> და <a href="https://www.smart-it.com/ge/privacy-policy/" target="_blank" rel="noreferrer">კონფიდენციალურობის პოლიტიკას</a>',
		submit: 'მოთხოვნის გაგზავნა',
		submitting: 'იგზავნება...',
		success: 'გმადლობთ. მალე დაგიკავშირდებით.',
		locked: 'ეს შედეგი უკვე ავტომატურად გაიგზავნა CRM-ში. დამატებითი საკონტაქტო მონაცემები ამ quiz-ისთვის აღარ მიიღება.',
		required: 'გთხოვთ შეავსოთ ეს ველი.',
		emailError: 'შეიყვანეთ სწორი e-mail.',
		phoneError: 'შეიყვანეთ სწორი ტელეფონის ნომერი.',
		acceptanceError: 'აუცილებელია პირობების მიღება.',
	},
};

const normalizeQuizLocale = (locale = 'ua') => {
	const normalized = String(locale || 'ua').toLowerCase().replace('_', '-');
	const baseLocale = normalized.split('-')[0];
	return baseLocale === 'uk' ? 'ua' : baseLocale;
};

const getLocaleTexts = (locale = 'en') => {
	const normalized = String(locale || 'en').toLowerCase().replace('_', '-');
	const alias = normalizeQuizLocale(normalized);

	return translations[normalized] || translations[alias] || translations.en;
};

const getCurrentContext = () => {
	const storedContext = window.CRMQuizLeadContext || {};
	const session = getQuestionnaireSession();
	return {
		...(session?.context || {}),
		...storedContext,
		locale: normalizeQuizLocale(storedContext.locale || session?.context?.locale || window.CRMQuizSettings?.locale || document.documentElement.lang || 'ua'),
		resultUrl:
			storedContext.resultUrl ||
			storedContext.result_url ||
			session?.context?.resultUrl ||
			session?.context?.result_url ||
			window.location.href,
		result_url:
			storedContext.result_url ||
			storedContext.resultUrl ||
			session?.context?.result_url ||
			session?.context?.resultUrl ||
			window.location.href,
		resultId: storedContext.resultId || storedContext.result_id || session?.context?.resultId || session?.context?.result_id || '',
		result_id: storedContext.result_id || storedContext.resultId || session?.context?.result_id || session?.context?.resultId || '',
		questionSummary: storedContext.questionSummary || session?.context?.questionSummary || [],
		message: '',
	};
};

const getInitialValues = (lead = {}) => ({
	'your-name': lead['your-name'] || lead.name || '',
	'your-lastname': lead['your-lastname'] || lead.lastname || '',
	'your-company': lead['your-company'] || lead.company || '',
	'your-position': lead['your-position'] || lead.position || '',
	'your-email': lead['your-email'] || lead.email || '',
	'your-phone': lead['your-phone'] || lead.phone || '',
	message: lead.message || '',
	'accept-this': lead['accept-this'] || lead.acceptance || '1',
});

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = (values, texts) => {
	const errors = {};
	const requiredFields = ['your-name', 'your-lastname', 'your-company', 'your-position', 'your-email', 'your-phone'];

	requiredFields.forEach((field) => {
		if (!String(values[field] || '').trim()) {
			errors[field] = texts.required;
		}
	});

	if (values['your-email'] && !emailPattern.test(String(values['your-email']).trim())) {
		errors['your-email'] = texts.emailError;
	}

	if (values['your-phone'] && !isPhoneValid(String(values['your-phone']).trim())) {
		errors['your-phone'] = texts.phoneError;
	}

	if (!values['accept-this']) {
		errors['accept-this'] = texts.acceptanceError;
	}

	return errors;
};

const buildHiddenFields = (context) => {
	const surveyAnswers = context.survey_answers || context.description || buildQuestionnaireAnswerText(context.questionSummary || []);

	return {
		dynamic_CF7_URL: window.location.href,
		landingsource: context.landingsource || '',
		landing_source: context.landing_source || context.landingsource || '',
		crm_result: context.crm_result || context.result?.recommendationTitle || context.result?.recommendation || '',
		survey_answers: surveyAnswers,
		quiz_result_id: context.resultId || context.result_id || '',
		quiz_result_url: context.resultUrl || context.result_url || '',
		quiz_recommendation: context.result?.recommendationTitle || context.result?.recommendation || '',
		quiz_recommendation_key: context.result?.recommendationKey || context.result?.recommendation_key || '',
		quiz_smart_score: context.result?.smartScore ?? context.result?.smart_score ?? '',
		quiz_d365_score: context.result?.d365Score ?? context.result?.d365_score ?? '',
		quiz_smart_pct: context.result?.smartPct ?? context.result?.smart_pct ?? '',
		quiz_d365_pct: context.result?.d365Pct ?? context.result?.d365_pct ?? '',
		quiz_locale: context.locale || '',
		quiz_description: context.description || surveyAnswers,
		quiz_answers: JSON.stringify(context.answers || []),
		landinglink: context.landinglink || window.location.href,
		utm_source: context.utm_source || '',
		utm_medium: context.utm_medium || '',
		utm_campaign: context.utm_campaign || '',
		utm_content: context.utm_content || '',
		utm_term: context.utm_term || '',
		userAgent: context.userAgent || navigator.userAgent || '',
		languageCode: context.languageCode || '',
		ip: context.ip || '',
	};
};

const QuestionnaireLeadForm = () => {
	const [context, setContext] = useState(() => getCurrentContext());
	const [values, setValues] = useState(() => getInitialValues(getCurrentContext()));
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [status, setStatus] = useState(getQuestionnaireStatus());
	const debugLog = (message, data = {}) => {
		window.__CRMQuizDebugLog?.('questionnaire-form', message, data);
	};

	const texts = useMemo(() => getLocaleTexts(context?.locale || 'en'), [context?.locale]);
	const hiddenFields = useMemo(() => buildHiddenFields(context || {}), [context]);
	const isFinalized = status === 'contact_submitted';

	useEffect(() => {
		debugLog('mounted', {
			contextKeys: Object.keys(context || {}),
			status,
			hiddenKeys: Object.keys(hiddenFields || {}),
		});
		window.__crmQuizQuestionnaireStatus = status;
		const stopAutoSubmit = startQuestionnaireAutoSubmit(getCurrentContext());

		const syncContext = () => {
			const currentContext = getCurrentContext();
			if (!currentContext) {
				debugLog('syncContext skipped: no current context');
				return;
			}

			const nextContext = ensureQuestionnaireSession(currentContext)?.context || currentContext;
			debugLog('syncContext', {
				contextKeys: Object.keys(nextContext || {}),
				status: getQuestionnaireStatus(),
			});
			setContext(nextContext);
			setValues((currentValues) => currentValues);
			const nextStatus = getQuestionnaireStatus();
			window.__crmQuizQuestionnaireStatus = nextStatus;
			setStatus(nextStatus);
		};

		syncContext();

		const unsubscribeStatus = subscribeQuestionnaireStatus(({ session, status: nextStatus }) => {
			debugLog('status event', {
				sessionId: session?.sessionId || '',
				status: nextStatus || session?.status || '',
			});
			if (session?.context) {
				setContext(session.context);
			}
			const resolvedStatus = nextStatus || session?.status || getQuestionnaireStatus();
			window.__crmQuizQuestionnaireStatus = resolvedStatus;
			setStatus(resolvedStatus);
		});

		const handleContextUpdate = () => {
			debugLog('context update event');
			syncContext();
		};

		window.addEventListener(CONTEXT_EVENT, handleContextUpdate);

		return () => {
			window.removeEventListener(CONTEXT_EVENT, handleContextUpdate);
			unsubscribeStatus();
			stopAutoSubmit();
		};
	}, []);

	const handleChange = (field, nextValue) => {
		setValues((current) => ({
			...current,
			[field]: nextValue,
		}));

		setErrors((current) => {
			if (!current[field]) {
				return current;
			}

			const nextErrors = { ...current };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const handleCheckboxChange = (event) => {
		handleChange('accept-this', event.target.checked ? '1' : '');
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
			debugLog('submit requested', {
				status,
				isSubmitting,
				isFinalized,
				visibleErrors: Object.keys(errors || {}),
		});

		if (isSubmitting || status === 'auto_submitting') {
			debugLog('submit blocked', {
				status,
				isSubmitting,
				isFinalized,
			});
			return;
		}

		const nextErrors = validateForm(values, texts);
		setErrors(nextErrors);
		debugLog('validation result', {
			errorKeys: Object.keys(nextErrors || {}),
		});

		if (Object.keys(nextErrors).length > 0) {
			return;
		}

		setIsSubmitting(true);
		debugLog('submit start', {
			contextKeys: Object.keys(context || {}),
			valueKeys: Object.keys(values || {}),
		});

		try {
			const response = await submitQuestionnaireLead(context, {
				'your-name': values['your-name'],
				'your-lastname': values['your-lastname'],
				'your-company': values['your-company'],
				'your-position': values['your-position'],
				'your-email': values['your-email'],
				'your-phone': values['your-phone'],
				message: values.message,
				'accept-this': values['accept-this'],
			});
			debugLog('submit success', {
				sessionStatus: response?.session?.status || '',
				responseKeys: Object.keys(response || {}),
			});

			setStatus('contact_submitted');
			setValues({
				'your-name': '',
				'your-lastname': '',
				'your-company': '',
				'your-position': '',
				'your-email': '',
				'your-phone': '',
				message: '',
				'accept-this': '',
			});
			setErrors({});
			setIsSubmitting(false);
			debugLog('form cleared after success', {
				status: 'contact_submitted',
			});

			const thankPopup = document.querySelector('#popup-thanks');
			if (!openPopupById('#popup-thanks', false) && !thankPopup) {
				setErrors((current) => ({
					...current,
				}));
			}
		} catch (error) {
			debugLog('submit error', {
				message: error instanceof Error ? error.message : String(error),
				stack: error instanceof Error ? error.stack : '',
			});
			setErrors((current) => ({
				...current,
				form: error.message || texts.success,
			}));
			setIsSubmitting(false);
		}
	};

	return (
		<div className="wpcf7">
			<form className="crm-quiz-custom-form" onSubmit={handleSubmit} noValidate>
				<div className="grid-2">
					<label>
						<span className="label">{texts.name}</span>
					<span className="wpcf7-form-control-wrap" data-name="your-name">
						<input
								size="40"
								maxLength="30"
								className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required${errors['your-name'] ? ' wpcf7-not-valid' : ''}`}
								aria-required="true"
								aria-invalid={Boolean(errors['your-name'])}
								placeholder={texts.name}
								value={values['your-name']}
								type="text"
								name="your-name"
							autoComplete="name"
							onChange={(event) => handleChange('your-name', event.target.value)}
						/>
						</span>
						{errors['your-name'] ? <span className="wpcf7-not-valid-tip">{errors['your-name']}</span> : null}
					</label>

					<label>
						<span className="label">{texts.lastname}</span>
						<span className="wpcf7-form-control-wrap" data-name="your-lastname">
							<input
								size="40"
								maxLength="30"
								className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required${errors['your-lastname'] ? ' wpcf7-not-valid' : ''}`}
								aria-required="true"
								aria-invalid={Boolean(errors['your-lastname'])}
								placeholder={texts.lastname}
								value={values['your-lastname']}
								type="text"
								name="your-lastname"
								onChange={(event) => handleChange('your-lastname', event.target.value)}
							/>
						</span>
						{errors['your-lastname'] ? <span className="wpcf7-not-valid-tip">{errors['your-lastname']}</span> : null}
					</label>

					<label>
						<span className="label">{texts.company}</span>
						<span className="wpcf7-form-control-wrap" data-name="your-company">
							<input
								size="40"
								maxLength="30"
								className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required${errors['your-company'] ? ' wpcf7-not-valid' : ''}`}
								aria-required="true"
								aria-invalid={Boolean(errors['your-company'])}
								placeholder={texts.company}
								value={values['your-company']}
								type="text"
								name="your-company"
								autoComplete="organization"
								onChange={(event) => handleChange('your-company', event.target.value)}
							/>
						</span>
						{errors['your-company'] ? <span className="wpcf7-not-valid-tip">{errors['your-company']}</span> : null}
					</label>

					<label>
						<span className="label">{texts.position}</span>
						<span className="wpcf7-form-control-wrap" data-name="your-position">
							<input
								size="40"
								maxLength="30"
								className={`wpcf7-form-control wpcf7-text wpcf7-validates-as-required${errors['your-position'] ? ' wpcf7-not-valid' : ''}`}
								aria-required="true"
								aria-invalid={Boolean(errors['your-position'])}
								placeholder={texts.position}
								value={values['your-position']}
								type="text"
								name="your-position"
								onChange={(event) => handleChange('your-position', event.target.value)}
							/>
						</span>
						{errors['your-position'] ? <span className="wpcf7-not-valid-tip">{errors['your-position']}</span> : null}
					</label>

					<label>
						<span className="label">{texts.email}</span>
						<span className="wpcf7-form-control-wrap" data-name="your-email">
							<input
								size="40"
								maxLength="50"
								className={`wpcf7-form-control wpcf7-email wpcf7-validates-as-required wpcf7-text wpcf7-validates-as-email${errors['your-email'] ? ' wpcf7-not-valid' : ''}`}
								aria-required="true"
								aria-invalid={Boolean(errors['your-email'])}
								placeholder={texts.email}
								value={values['your-email']}
								type="email"
								name="your-email"
								autoComplete="email"
								onChange={(event) => handleChange('your-email', event.target.value)}
							/>
						</span>
						{errors['your-email'] ? <span className="wpcf7-not-valid-tip">{errors['your-email']}</span> : null}
					</label>

					<label>
						<span className="label label-phone">{texts.phone}</span>
						<span className="wpcf7-form-control-wrap" data-name="your-phone">
							<PhoneInput
								value={values['your-phone']}
								onChange={(phone) => handleChange('your-phone', phone)}
								error={errors['your-phone']}
								placeholder={texts.phone}
								defaultCountry={context.locale === 'pl' ? 'pl' : context.locale === 'en' ? 'gb' : context.locale === 'az' ? 'az' : context.locale === 'ge' ? 'ge' : 'ua'}
							/>
						</span>
						{errors['your-phone'] ? <span className="wpcf7-not-valid-tip">{errors['your-phone']}</span> : null}
					</label>

					<label className="w100">
						<span className="label">
							{texts.comment} <span>{texts.commentHint}</span>
						</span>
						<span className="wpcf7-form-control-wrap" data-name="message">
							<textarea
								cols="40"
								rows="1"
								maxLength="140"
								className="wpcf7-form-control wpcf7-textarea"
								aria-invalid="false"
								placeholder={texts.comment}
								name="message"
								value={values.message}
								onChange={(event) => handleChange('message', event.target.value)}
							/>
						</span>
					</label>

					<span className="wpcf7-form-control-wrap accept-this" data-name="accept-this">
						<span className="wpcf7-form-control wpcf7-acceptance">
							<span className="wpcf7-list-item">
								<label>
									<input type="checkbox" name="accept-this" value="1" checked={Boolean(values['accept-this'])} onChange={handleCheckboxChange} aria-invalid={Boolean(errors['accept-this'])} />
									<span className="wpcf7-list-item-label" dangerouslySetInnerHTML={{ __html: texts.acceptance }} />
								</label>
							</span>
						</span>
						{errors['accept-this'] ? <span className="wpcf7-not-valid-tip">{errors['accept-this']}</span> : null}
					</span>

					<input type="hidden" name="dynamic_CF7_URL" id="dynamic_CF7_URL" value={hiddenFields.dynamic_CF7_URL} readOnly />
					<input type="hidden" name="landingsource" id="landingsource" value={hiddenFields.landingsource} readOnly />
					<input type="hidden" name="landing_source" id="landing_source" value={hiddenFields.landing_source} readOnly />
					<input type="hidden" name="crm_result" id="crm-result" value={hiddenFields.crm_result} readOnly />
					<input type="hidden" name="survey_answers" id="survey-answers" value={hiddenFields.survey_answers} readOnly />
					<input type="hidden" name="quiz_result_id" id="quiz-result-id" value={hiddenFields.quiz_result_id} readOnly />
					<input type="hidden" name="quiz_result_url" id="quiz-result-url" value={hiddenFields.quiz_result_url} readOnly />
					<input type="hidden" name="quiz_recommendation" id="quiz-recommendation" value={hiddenFields.quiz_recommendation} readOnly />
					<input type="hidden" name="quiz_recommendation_key" id="quiz-recommendation-key" value={hiddenFields.quiz_recommendation_key} readOnly />
					<input type="hidden" name="quiz_smart_score" id="quiz-smart-score" value={hiddenFields.quiz_smart_score} readOnly />
					<input type="hidden" name="quiz_d365_score" id="quiz-d365-score" value={hiddenFields.quiz_d365_score} readOnly />
					<input type="hidden" name="quiz_smart_pct" id="quiz-smart-pct" value={hiddenFields.quiz_smart_pct} readOnly />
					<input type="hidden" name="quiz_d365_pct" id="quiz-d365-pct" value={hiddenFields.quiz_d365_pct} readOnly />
					<input type="hidden" name="quiz_locale" id="quiz-locale" value={hiddenFields.quiz_locale} readOnly />
					<input type="hidden" name="quiz_description" id="quiz-description" value={hiddenFields.quiz_description} readOnly />
					<input type="hidden" name="quiz_answers" id="quiz-answers" value={hiddenFields.quiz_answers} readOnly />
					<input type="hidden" name="landinglink" id="landinglink" value={hiddenFields.landinglink} readOnly />
					<input type="hidden" name="utm_source" id="utm_source" value={hiddenFields.utm_source} readOnly />
					<input type="hidden" name="utm_medium" id="utm_medium" value={hiddenFields.utm_medium} readOnly />
					<input type="hidden" name="utm_campaign" id="utm_campaign" value={hiddenFields.utm_campaign} readOnly />
					<input type="hidden" name="utm_content" id="utm_content" value={hiddenFields.utm_content} readOnly />
					<input type="hidden" name="utm_term" id="utm_term" value={hiddenFields.utm_term} readOnly />
					<input type="hidden" name="userAgent" id="userAgent" value={hiddenFields.userAgent} readOnly />
					<input type="hidden" name="languageCode" id="languageCode" value={hiddenFields.languageCode} readOnly />
					<input type="hidden" name="ip" id="ip" value={hiddenFields.ip} readOnly />

						<button className="wpcf7-form-control wpcf7-submit has-spinner bttn" type="submit">
							{isSubmitting ? texts.submitting : texts.submit}
						</button>
				</div>

				{errors.form ? <div className="wpcf7-response-output">{String(errors.form)}</div> : null}

				{isFinalized ? (
					<div className="crm-quiz-custom-form__state">
						<div className="wpcf7-response-output">{status === 'contact_submitted' ? texts.success : texts.locked}</div>
					</div>
				) : null}
			</form>
		</div>
	);
};

export const mountCrmQuizQuestionnaireForm = () => {
	if (window.__crmQuizQuestionnaireFormMounted) {
		return;
	}

	const rootElement = document.querySelector('[data-crm-quiz-form-root]');
	if (!rootElement) {
		return;
	}

	window.__crmQuizQuestionnaireFormMounted = true;
	createRoot(rootElement).render(<QuestionnaireLeadForm />);
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', mountCrmQuizQuestionnaireForm);
} else {
	mountCrmQuizQuestionnaireForm();
}
